import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAtlas } from "@/context/AtlasContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function BrandedLogin() {
  const { slug } = useParams();
  const { login, applyTenant } = useAtlas();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/companies/public/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null)); }, [slug]);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email.trim(), password); window.location.assign("/"); }
    catch (err) { toast.error(apiError(err, "Login failed")); }
    finally { setLoading(false); }
  };

  if (brand === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  if (brand === null) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500" data-testid="branded-login-missing">
      <p className="text-sm">This sign-in address isn't available.</p>
      <Link to="/login" className="text-sm text-blue-600 hover:underline">Go to the main sign-in</Link>
    </div>
  );
  const accent = brand.accent || "#4f46e5";
  const initials = (brand.name || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="branded-login-page">
      <div className="hidden lg:flex flex-col justify-center p-14 text-white relative overflow-hidden" style={{ background: accent }}>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(700px 400px at 100% 0%, rgba(255,255,255,.4), transparent 60%)" }} />
        <div className="relative">
          {brand.logo ? <img src={brand.logo} alt="" className="w-16 h-16 rounded-2xl object-cover mb-6" /> : <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center font-heading text-2xl font-bold mb-6">{initials}</div>}
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">{brand.name}</h1>
          <p className="text-white/80 mt-3 text-lg">Sign in to your workspace.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6" style={{ background: "var(--paper)" }}>
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 rise" data-testid="branded-login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-4">
            {brand.logo ? <img src={brand.logo} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center font-bold" style={{ background: accent }}>{initials}</div>}
            <span className="font-heading font-bold text-lg">{brand.name}</span>
          </div>
          <div><p className="eyebrow mb-1">Welcome</p><h2 className="font-heading text-3xl font-semibold">Sign in</h2></div>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="h-11 bg-white" data-testid="branded-login-email" /></div>
          <div className="space-y-2"><div className="flex justify-between"><Label htmlFor="password">Password</Label><Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-900">Forgot?</Link></div><Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="h-11 bg-white" data-testid="branded-login-password" /></div>
          <Button type="submit" className="w-full h-11 rounded-full text-white" style={{ background: accent }} disabled={loading} data-testid="branded-login-submit">{loading ? "Signing in…" : "Sign in"}</Button>
        </form>
      </div>
    </div>
  );
}
